import React, { useRef, useImperativeHandle, forwardRef, useEffect, useMemo } from "react";
import { StriimFormBuilder, FIELD_TYPES, StriimTheme, StriimTooltip } from "@striim/striim-ui";
import FileBrowser from "../filebrowser-v2/filebrowser";
import _ from "underscore";
import Password from "../fd-password/fd-password";
import xss from "xss";
import ConnectionProfileSelector from "../connection-profile-selector/connection-profile-selector";
import sxClasses from "./segmentation.styles";
import { SvgIcon } from "@mui/material";
import JsonInput from "../json-input/json-input";
import InfoIcon from "app/images/striimline/info-icon.svg";
import propertyTemplateService from "core/services/metaStoreService/property-template-service";
import ActionButton, { TEST_PRIMARY_CONNECTION } from "./action-button";
import SelectTables from "../../modules/flow-designer/vts/selectTables";
import SelectTablesProvider from "../../modules/flow-designer/vts/select-tables-context";
import { PRESERVED_NAMES } from "core/constants";
import App from "app";
import { AppVentFDAdapterOnChange, AppVentVtsDependencyFieldsChanged } from "./fd-form-builder.utils";
import { useSelectTableConfigs } from "../../modules/flow-designer/vts/selectTables-utils";
import useDebounce from "../../hooks/useDebounce";
import { getFormValues } from "./fd-form-builder-utils";
import { vtsDependencyFields } from "./vts-reset-modal-dependency-fields";
import { Provider } from "react-redux";
import store from "src/stores/redux/store";

const FormBuilder = (
    {
        initialValues,
        formFields,
        propertiesModel,
        jsonOrMapFields,
        handler,
        isFormEnabled,
        metaObjectId,
        hasVisualTableSelection,
        isCacheComponent
    },
    ref
) => {
    const { adapterName, dbType, entityType, schemaType, isCDC } = useSelectTableConfigs(handler, initialValues);

    const triggerRef = useRef(null);
    const formRef = useRef();
    const prevRef = useRef();
    useImperativeHandle(ref, () => ({
        validateForm: () =>
            formRef.current
                .submitForm()
                .then(formRef.current.validateForm)
                .then(errors => {
                    return errors;
                })
    }));

    useEffect(() => {
        App.vent.on("save:adapter:defaults", function() {
            const stringifiedValues = getFormValues(formRef.current.values, jsonOrMapFields);
            propertiesModel.set(stringifiedValues);
        });
        return () => {
            App.vent.off("save:adapter:defaults");
            triggerRef.current = false;
        };
    }, []);

    const triggerAppVent = useDebounce(() => App.vent.trigger(AppVentFDAdapterOnChange), 200);

    const prepareForComparison = obj => {
        const withoutAdditionalProperties = _.omit(obj, ["encrypted"]);
        const normalized = {};
        // Normalize undefined to null for consistent comparison as fields without a value would have undefined as value while its null on the initial value
        for (const [key, value] of Object.entries(withoutAdditionalProperties)) {
            normalized[key] = value === undefined ? null : value;
        }
        return normalized;
    };

    function onChange(values) {
        //onchange is called initially since we are changing the values before setting to the form
        let stringifiedValues = getFormValues(values, jsonOrMapFields);
        if (!triggerRef.current) {
            prevRef.current = values;
            triggerRef.current = true;
            return;
        }

        if (hasVisualTableSelection) {
            let currentValuesForComparison, prevValuesForComparison;
            prevValuesForComparison = prevRef.current;
            currentValuesForComparison = prepareForComparison(values);
            prevValuesForComparison = prepareForComparison(prevValuesForComparison);

            if (_.isEqual(currentValuesForComparison, prevValuesForComparison)) {
                propertiesModel.set(stringifiedValues);
                prevRef.current = values;
                return;
            }

            const diffKeys = Object.keys(currentValuesForComparison).filter(
                key => !_.isEqual(currentValuesForComparison[key], prevValuesForComparison[key])
            );

            const updatePropertiesmodel = () => {
                propertiesModel.set(stringifiedValues);
                triggerAppVent();
            };
            const cancelModal = () => {};
            const visualTableSelectionNotFilledCb = () => {
                propertiesModel.set(stringifiedValues);
                prevRef.current = values;
                triggerAppVent();
            };

            const containsConnectionField = _.intersection(diffKeys, vtsDependencyFields);
            if (containsConnectionField.length > 0) {
                const valuesBeforeChange = {};
                containsConnectionField.forEach(fieldName => {
                    valuesBeforeChange[fieldName] = prevValuesForComparison[fieldName];
                });
                prevRef.current = values;
                App.vent.trigger(AppVentVtsDependencyFieldsChanged, {
                    prevValues: structuredClone(valuesBeforeChange),
                    fieldsChanged: containsConnectionField,
                    proceedCb: updatePropertiesmodel,
                    cancelCb: cancelModal,
                    visualTableSelectionNotFilledCb: visualTableSelectionNotFilledCb
                });

                return;
            }
            propertiesModel.set(stringifiedValues);
            prevRef.current = values;
            triggerAppVent();
        } else {
            propertiesModel.set(stringifiedValues);
            prevRef.current = values;
        }
    }

    const connectionFields = useMemo(() => {
        let buttons = [];
        let connectionFields = [];
        if (adapterName) {
            buttons = _.filter(propertyTemplateService.propertyActionButtons, function(item) {
                return item.adapterName === adapterName;
            });

            buttons.forEach(function(button) {
                const { disabled, group, viewWeight, visibility, name } = button;

                formFields.push({
                    disabled,
                    group,
                    viewWeight,
                    visibility,
                    required: name !== TEST_PRIMARY_CONNECTION,
                    type: "component",
                    component: (
                        /* Caution! Updated props not triggering component rerender (formFields from Marionette) */
                        <ActionButton
                            button={button}
                            handler={handler}
                            propertiesModel={propertiesModel}
                            metaObjectId={metaObjectId}
                            isFormEnabled={isFormEnabled}
                            formRef={formRef}
                        />
                    ),
                    isActionBtn: true
                });
            });
        }
        if (
            !!buttons?.length &&
            buttons.some(button => button.name === "TEST_PRIMARY_CONNECTION" || button.name === "TEST_CONNECTION")
        ) {
            const testConnectionButton = buttons.find(
                button => button.name === "TEST_PRIMARY_CONNECTION" || button.name === "TEST_CONNECTION"
            );
            connectionFields = formFields.filter(
                field => testConnectionButton.parameters.includes(field.name) && !!field.required
            );
        }

        return connectionFields;
    }, [adapterName, handler, propertiesModel, metaObjectId, isFormEnabled, formRef]);

    return (
        <Provider store={store}>
            <StriimTheme preselector=".striim.materialize.light:not(#\20)">
                <StriimFormBuilder
                    formProps={{}}
                    onChange={onChange}
                    initialFieldValues={initialValues}
                    data={formFields}
                    validateOnChange={false}
                    previewPassword={true}
                    setFieldToDefaultOnHidden={true}
                    formRef={formRef}
                    customFields={{
                        [FIELD_TYPES.CONNECTIONPROFILE]: props => <ConnectionProfileSelector {...props} />,
                        [FIELD_TYPES.FILE]: props => <FileBrowser directoryOnly={false} {...props} />,
                        [FIELD_TYPES.JSON]: props => <JsonInput {...props} />,
                        [FIELD_TYPES.PROPERTYTEMPLATE_PASSWORD]: props => <Password {...props} />,
                        [FIELD_TYPES.SELECTTABLES]: props => (
                            <SelectTablesProvider>
                                <SelectTables
                                    {...props}
                                    formRef={formRef}
                                    dbType={dbType}
                                    schemaType={schemaType}
                                    entityType={entityType}
                                    isCDC={isCDC}
                                    metaObjectId={metaObjectId}
                                    handler={handler}
                                    connectionFields={connectionFields}
                                    adapterName={adapterName}
                                    isCacheComponent={isCacheComponent}
                                    formFields={formFields}
                                />
                            </SelectTablesProvider>
                        )
                    }}
                    preservedNames={PRESERVED_NAMES}
                    usePropertyPanelLayout
                />
            </StriimTheme>
        </Provider>
    );
};
export default forwardRef(FormBuilder);

const classes = {
    tooltipIcon: {
        height: "15px",
        width: "15px",
        fill: "none",
        cursor: "pointer"
    }
};

export const TooltipComponent = ({ description }) =>
    description ? (
        <StriimTooltip
            title={<div style={sxClasses.fdTooltip} dangerouslySetInnerHTML={{ __html: xss(description) }} />}
            arrow
            placement="left"
        >
            <div style={{ display: "flex" }}>
                <SvgIcon component={InfoIcon} sx={classes.tooltipIcon} />
            </div>
        </StriimTooltip>
    ) : null;
